import { IconCollection } from "components/Icon/IconCollection";
import RecordLabel from "components/RecordLabel";
import { ResourceConfig } from "config/config";
import {
    deleteActionSignature,
    DeleteListAction,
    deletePermanentlyActionSignature,
    duplicateActionSignature,
    exportActionSignature,
    moveToLocationActionSignature,
    PermanentlyDeleteListAction,
} from "containers/Resources/components/CommonActions/CommonActions";
import {
    commentsTab,
    holdingTheLockTab,
    logsTab,
    logsTabKey,
    metadataTab,
    waitingForLockTab,
} from "containers/Resources/screens/ResourcesSingle/constants";
import { getRelativeUrl } from "utils/service";
import { isYamlEqual, parseYaml, serializeToPrettyYaml } from "utils/valueTransformers";
import resource from "./resource";

const resourceName = "setting";

const config: ResourceConfig = {
    label: "Setting",
    icon: IconCollection.tune,
    resourceName,
    resourceClass: "resource",
    defaultActiveTab: logsTabKey,
    layout: [
        { partial: "pageHeaderForm" },
        { partial: "form" },
        {
            partial: "tabs",
            config: {
                tabs: [
                    logsTab.tab,
                    {
                        ...holdingTheLockTab.tab,
                        docs: { title: "Settings and Locks", url: "/docs/settings-and-locks" },
                    },
                    waitingForLockTab.tab,
                    metadataTab.tab,
                    commentsTab.tab,
                ],
                content: [
                    logsTab.content,
                    holdingTheLockTab.content,
                    waitingForLockTab.content,
                    metadataTab.content,
                    commentsTab.content,
                ],
            },
        },
    ],
    graphql: {
        resource: resourceName,
        resourceBody: `
            ${resource.graphql.resourceBody}
            value
            execution_id
            execution_id_execution {
                id
                name
            }
        `,
        resourceBodyListing: `
            ${resource.graphql.resourceBody}
            execution_id_execution {
                name
                id
            }
        `,
        createVariables: `
            ${resource.graphql.createVariables(resourceName)}
            $value: SchemalessData,
        `,
        createQuery: `
            ${resource.graphql.createQuery(resourceName)}
            value: $value,
        `,
        updateVariables: `
            ${resource.graphql.updateVariables(resourceName)}
            $value: SchemalessData,
        `,
        updateQuery: `
            ${resource.graphql.updateQuery(resourceName)}
            value: $value,
        `,
    },
    additionalColumns: () => [
        {
            label: "Used by",
            key: "used_by",
            shown: true,
            sortable: false,
            dto: (value) => {
                if (value?.execution_id_execution) {
                    const { name, id } = value.execution_id_execution;
                    return (
                        <RecordLabel
                            name={name}
                            link={getRelativeUrl("execution", id)}
                            iconByType="execution"
                        />
                    );
                }
                return null;
            },
        },
    ],
    actions: {
        shown: [],
        hidden: [
            duplicateActionSignature,
            exportActionSignature,
            moveToLocationActionSignature,
            deleteActionSignature,
            deletePermanentlyActionSignature,
        ],
    },
    listActions: {
        shown: [],
        hidden: [
            { key: "delete", action: DeleteListAction },
            { key: "deletePermanently", action: PermanentlyDeleteListAction },
        ],
    },
    form: [
        {
            name: "value",
            component: "codeEditor",
            fieldProps: {
                lang: "yaml",
                isReadOnly: false,
                // Editable JSON⇄YAML display-mode toggle. The value is seeded as
                // YAML (initialValueDTO) and submitted via parseYaml/isYamlEqual,
                // both of which parse JSON too (a YAML subset), so switching the
                // display representation never marks the form dirty and needs no
                // change to the submit/dirty path.
                structuredDataEdit: true,
            },
            submitDto: (value: any) => parseYaml(value),
            isEqual: isYamlEqual,
        },
    ],
    formDTO: {
        initialValueDTO: (data: any) => {
            const value = serializeToPrettyYaml(data?.value);
            const usedBy = {
                execution: data?.execution_id_execution,
            };
            return { ...data, value, usedBy };
        },
    },
};
export default config;
